import { MenuItem } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { ItemRendererProps, MultiSelect2 } from '@blueprintjs/select';
import * as React from 'react';
import { NoResultsMenuItem } from 'vet-bones/components';
import { SelectItem, selectItemPredicate } from 'vet-bones/utils/select';

import { TEST_IDS } from 'src/constants/testIds';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { useUpdateMappingUniqueKeySets } from 'src/containers/ProjectMapping/hooks/useUpdateMappingUniqueKeySets';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { projectResourceFieldItemsSelector } from 'src/selectors/projectResource';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectMappingUniqueKeySetsMultiSelectProps = {
  disabled?: boolean;
  mapping: Mapping;
};

export const ProjectMappingUniqueKeySetsMultiSelect: React.VFC<
  ProjectMappingUniqueKeySetsMultiSelectProps
> = ({ disabled, mapping }) => {
  const { projectId } = useRouterParams();

  // projectNodeDataSelector is a factory with a non-serializable arg, so it
  // can't be memoized at the source; memoize the instance here instead.
  const resourceDataSelector = React.useMemo(
    () => projectNodeDataSelector(projectId, mapping.from, isResource),
    [projectId, mapping.from]
  );
  const resourceData = useReduxSelector(resourceDataSelector);

  const fieldItems = useReduxSelector(
    projectResourceFieldItemsSelector(
      projectId,
      mapping.from ? getResourceValue(mapping.from) : undefined,
      resourceData?.type
    )
  );
  const updateUniqueKeySets = useUpdateMappingUniqueKeySets(mapping.key);

  const selectedItems = React.useMemo(
    () =>
      mapping.uniqueKeySets
        .map((field) => fieldItems.find((item) => item.value === field))
        .filter((item): item is SelectItem => Boolean(item)),
    [fieldItems, mapping]
  );

  const onRemove = React.useCallback(
    (item: SelectItem) => {
      updateUniqueKeySets(
        mapping.uniqueKeySets.filter((f) => f !== item.value)
      );
    },
    [mapping, updateUniqueKeySets]
  );

  const onSelect = React.useCallback(
    (item: SelectItem) => {
      if (!mapping.uniqueKeySets.includes(item.value)) {
        updateUniqueKeySets([...mapping.uniqueKeySets, item.value]);
      } else {
        onRemove(item);
      }
    },
    [mapping, onRemove, updateUniqueKeySets]
  );

  const itemRenderer = (
    item: SelectItem,
    { handleClick, modifiers }: ItemRendererProps
  ) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <MenuItem
        key={item.value}
        active={modifiers.active}
        icon={
          mapping.uniqueKeySets.includes(item.value)
            ? IconNames.TICK
            : IconNames.BLANK
        }
        onClick={handleClick}
        text={item.text}
      />
    );
  };

  return (
    <MultiSelect2<SelectItem>
      data-testid={TEST_IDS.projectMappingSettingsDrawer.uniqueKeySets}
      disabled={disabled}
      fill
      itemPredicate={selectItemPredicate()(fieldItems)}
      itemRenderer={itemRenderer}
      items={fieldItems}
      noResults={
        <NoResultsMenuItem text={copy.components.defaults.noResults} />
      }
      onItemSelect={onSelect}
      onRemove={onRemove}
      placeholder={
        copy.components.projectMappingSettingsDrawer.uniqueKeySetsPlaceholder
      }
      popoverProps={{ minimal: true, popoverClassName: 'sd-popover-menu' }}
      resetOnSelect
      selectedItems={selectedItems}
      tagRenderer={(item) => item.text}
    />
  );
};
